import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface HeaderProps {
  title: string;
  enableBack?: boolean;
}

export default function Header({ title, enableBack }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  const handleBack = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  // Helper for active link styles
  const getLinkStyles = (path: string, color: 'blue' | 'purple' | 'emerald' | 'amber') => {
    const active = isActive(path);
    const base = "flex items-center gap-2 px-4 py-2 rounded-2xl font-black text-sm transition-all border-b-4 active:border-b-0 active:translate-y-1 transform";

    const colors = {
      blue: active ? "bg-blue-400 border-blue-600 text-white" : "bg-white border-blue-100 text-blue-400 hover:bg-blue-50",
      purple: active ? "bg-purple-400 border-purple-600 text-white" : "bg-white border-purple-100 text-purple-400 hover:bg-purple-50",
      emerald: active ? "bg-emerald-400 border-emerald-600 text-white" : "bg-white border-emerald-100 text-emerald-400 hover:bg-emerald-50",
      amber: active ? "bg-amber-400 border-amber-600 text-white" : "bg-white border-amber-100 text-amber-400 hover:bg-amber-50",
    };

    return `${base} ${colors[color]}`;
  };

  return (
    // Using ! classes to override potential global styles targeting <header>
    <header className="!static !bg-none !h-auto !w-full !max-w-none !m-0 !p-0 !shadow-none !border-none !rounded-none z-50 mb-6">
      <div className="w-full bg-sky-300 pt-6 pb-8 px-4 rounded-b-[3rem] shadow-[0_10px_0_rgba(14,165,233,0.2)] border-b-8 border-sky-400 relative overflow-hidden">

        {/* Decorative background elements */}
        <div className="absolute top-[-20%] left-[-10%] w-56 h-56 bg-sky-200 rounded-full opacity-60 blur-2xl pointer-events-none mix-blend-screen" />
        <div className="absolute bottom-[-10%] right-[-5%] w-40 h-40 bg-white rounded-full opacity-20 blur-xl pointer-events-none mix-blend-overlay" />

        <div className="relative max-w-lg mx-auto flex flex-col gap-5">

          {/* Top Row: Back Button & Title */}
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 flex items-center justify-center">
              {enableBack && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="group flex items-center justify-center w-12 h-12 bg-amber-400 hover:bg-amber-300 rounded-2xl border-b-4 border-amber-600 active:border-b-0 active:translate-y-1 transition-all shadow-lg"
                  aria-label="Back"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-amber-900 group-hover:-rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Title with playful stroke effect */}
            <h1
              className="text-3xl font-[900] text-white tracking-wider uppercase text-center flex-1 mx-2 truncate"
              style={{
                textShadow: '2px 2px 0px #0ea5e9, -2px -2px 0px #0ea5e9, 2px -2px 0px #0ea5e9, -2px 2px 0px #0ea5e9, 0 4px 0 rgba(0,0,0,0.1)'
              }}
            >
              {title}
            </h1>

            <div className="w-12" /> {/* Spacer for centering */}
          </div>

          {/* Nav Pills */}
          <nav className="flex justify-center gap-3">
            <Link to="/" className={getLinkStyles('/', 'emerald')}>
              <span className="text-lg filter drop-shadow-sm">🏠</span>
              <span>Home</span>
            </Link>
            <Link to="/history" className={getLinkStyles('/history', 'purple')}>
              <span className="text-lg filter drop-shadow-sm">📜</span>
              <span>History</span>
            </Link>
            <button 
              onClick={handleLogout} 
              className="flex items-center gap-2 px-4 py-2 rounded-2xl font-black text-sm transition-all border-b-4 active:border-b-0 active:translate-y-1 transform bg-red-400 border-red-600 text-white hover:bg-red-500 shadow-sm"
              aria-label="Logout"
            >
              <span className="text-lg filter drop-shadow-sm">🚪</span>
              <span>Keluar</span>
            </button>
          </nav>

        </div>
      </div>
    </header>
  );
}
